import { observer } from "mobx-react-lite";

import { FunctionNode } from "@/components/expression/FunctionNode";
import { GroupNode } from "@/components/expression/GroupNode";
import { MeasuredItem } from "@/components/expression/inline-node/ItemRenderer";
import { RowContent } from "@/components/expression/inline-node/wrapping/RowContent";
import { FlatNode } from "@/components/expression/utils/FlatNode";
import { VariableNode } from "@/components/expression/VariableNode";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

import { bracketsFromSlice } from "./buildRowTree";

import type { RowNode, WrappableContainer } from "./types";

const GroupFragment = observer(function GroupFragment({
  node,
  onItemSize,
}: {
  node: Extract<RowNode, { kind: "group" }>;
  onItemSize?: (key: string, width: number) => void;
}) {
  const { container, slice, children } = node;

  const innerContent = (
    <div className="flex items-baseline relative">
      <RowContent content={children} onItemSize={onItemSize} />
    </div>
  );

  if (container.is(FunctionNodeModel)) {
    return (
      <FunctionNode
        node={container}
        slice={slice}
        symbolWrapper={(symbol) => {
          if (onItemSize) {
            return (
              <MeasuredItem
                itemKey={`${container.id}-symbol`}
                className="flex flex-row items-center"
                onSize={onItemSize}
              >
                {symbol}
              </MeasuredItem>
            );
          }

          return symbol;
        }}
      >
        {innerContent}
      </FunctionNode>
    );
  }

  if (container.is(VariableNodeModel)) {
    return (
      <VariableNode
        node={container}
        slice={slice}
        symbolWrapper={(symbol) => {
          if (onItemSize) {
            return (
              <MeasuredItem
                itemKey={`${container.id}-symbol`}
                className="flex flex-row items-center"
                onSize={onItemSize}
              >
                {symbol}
              </MeasuredItem>
            );
          }

          return symbol;
        }}
      >
        {innerContent}
      </VariableNode>
    );
  }

  return (
    <GroupNode node={container} slice={slice}>
      {innerContent}
    </GroupNode>
  );
});

export const GroupRowNode = observer(function GroupRowNode({
  node,
  onItemSize,
  externallyHandledTrailingFor,
}: {
  node: Extract<RowNode, { kind: "group" }>;
  onItemSize?: (key: string, width: number) => void;
  externallyHandledTrailingFor?: InlineNodeModel;
}) {
  const { container, slice } = node;
  const parentInline = getContainerParentInline(container);
  const inlineIndex = parentInline ? container.index : -1;
  const { showLeft, showRight } = bracketsFromSlice(slice);

  const fragment = <GroupFragment node={node} onItemSize={onItemSize} />;

  const wrapped =
    parentInline && inlineIndex >= 0 && showLeft ? (
      <FlatNode
        inlineNode={parentInline}
        index={inlineIndex}
        cursorPosition="start"
        cursorClassName={inlineIndex > 0 ? "-translate-x-[1px]" : undefined}
      >
        {fragment}
      </FlatNode>
    ) : (
      fragment
    );

  const isLastInInline =
    parentInline !== undefined && inlineIndex === parentInline.nodes.length - 1;
  const externallyHandled = !!parentInline && parentInline === externallyHandledTrailingFor;
  const trailingAnchor =
    parentInline && showRight && isLastInInline && !externallyHandled ? (
      <FlatNode key="group-trailing" inlineNode={parentInline} index={parentInline.nodes.length} />
    ) : null;

  return (
    <>
      {wrapped}
      {trailingAnchor}
    </>
  );
});

function getContainerParentInline(container: WrappableContainer): InlineNodeModel | undefined {
  const { parent } = container;
  return parent && parent.is(InlineNodeModel) ? parent : undefined;
}
